import { useCallback, useEffect } from "react";
import type { Editor } from "@tiptap/core";
import { Save } from "lucide-react";

import type { DocumentBlock } from "@/api/document/block";
import { editorBlocks } from "@/components/document-editor/editor";
import { Button } from "@/components/ui/button";
import { useManualEdit } from "@/hooks/use-manuals";

interface SaveActionProps {
	manualId: number;
	editor: Editor;
	dirty: boolean;
	onSaved(blocks: DocumentBlock[]): void;
}

export function SaveAction({ manualId, editor, dirty, onSaved }: SaveActionProps) {
	const edit = useManualEdit();

	const disabled = !dirty || edit.isPending;

	const save = useCallback(() => {
		if (!dirty || edit.isPending) {
			return;
		}

		const blocks = editorBlocks(editor);

		edit.mutate({ id: manualId, blocks }, { onSuccess: () => onSaved(blocks) });
	}, [dirty, edit, editor, manualId, onSaved]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
				event.preventDefault();
				save();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [save]);

	return (
		<Button size="sm" className="h-11 md:h-8" disabled={disabled} onClick={save}>
			<Save />
			{edit.isPending && "Salvando manual..."}
			{dirty && !edit.isPending && "Salvar manual"}
			{!dirty && "Salvo"}
		</Button>
	);
}
